import { VULNERABILITY_CODE_COLOR } from "../../constants/index";
import { useTheme } from "@hooks/useTheme";

const Badge = ({
  children,
  variant = "default",
  size = "sm",
  className = "",
  bgColor,
  "data-testid": dataTestId,
  ...props
}) => {
  const { colors } = useTheme();

  const baseClasses = "inline-flex items-center justify-center font-medium rounded transition-colors";

  const sizeClasses = {
    xs: "px-1.5 py-0.5 text-xs",
    sm: "px-2 py-1 text-xs",
    md: "px-3 py-1.5 text-sm",
    lg: "px-4 py-2 text-base",
  };

  const variantClasses = {
    default: colors.badgeVariantDefault,
    primary: colors.badgeVariantPrimary,
    secondary: colors.badgeVariantSecondary,
    success: colors.badgeVariantSuccess,
    warning: colors.badgeVariantWarning,
    danger: colors.badgeVariantDanger,
    info: colors.badgeVariantInfo,
    // Specific variants for component list
    conflict_p: colors.badgeVariantConflict,
    conflict_l: colors.badgeVariantConflict,
    obligation_1: colors.badgeVariantObligation1,
    obligation_2: colors.badgeVariantObligation2,
    obligation_5: colors.badgeVariantObligation5,
    // Vulnerability codes with custom colors
    vuln_S: colors.badgeVariantVulnText,
    vuln_A: colors.badgeVariantVulnText,
    vuln_B: colors.badgeVariantVulnText,
    vuln_C: colors.badgeVariantVulnText,
    vuln_D: colors.badgeVariantVulnText,
    vuln_E: colors.badgeVariantVulnText,
    vuln_F: colors.badgeVariantVulnText,
    vuln_G: colors.badgeVariantVulnText,
    vuln_H: colors.badgeVariantVulnText,
    vuln_I: colors.badgeVariantVulnText,
    vuln_J: colors.badgeVariantVulnText,
    vuln_K: colors.badgeVariantVulnText,
    vuln_L: colors.badgeVariantVulnText,
    vuln_M: colors.badgeVariantVulnText,
    vuln_N: colors.badgeVariantVulnText,
    vuln_O: colors.badgeVariantVulnText,
    vuln_critical: colors.badgeVariantVulnCritical,
    vuln_high: colors.badgeVariantVulnHigh,
    vuln_medium: colors.badgeVariantVulnMedium,
  };

  // Get custom background color for vulnerability codes
  const getCustomStyle = () => {
    if (bgColor && VULNERABILITY_CODE_COLOR[bgColor]) {
      return { backgroundColor: VULNERABILITY_CODE_COLOR[bgColor] };
    }
    return {};
  };

  return (
    <span
      data-testid={dataTestId}
      className={` ${baseClasses} ${sizeClasses[size]} ${variantClasses[variant] || colors.badgeVariantDefault} ${className} `}
      style={getCustomStyle()}
      {...props}
    >
      {children}
    </span>
  );
};

// Specialized badge components
export const ConflictBadge = ({ type, className = "", "data-testid": dataTestId, ...props }) => (
  <Badge
    variant={`conflict_${type.toLowerCase()}`}
    size="xs"
    className={`h-8 w-8 ${className}`}
    data-testid={dataTestId ?? `conflict-badge-${type.toLowerCase()}`}
    {...props}
  >
    {type}
  </Badge>
);

export const ObligationBadge = ({ number, className = "", "data-testid": dataTestId, ...props }) => (
  <Badge
    variant={`obligation_${number}`}
    size="xs"
    className={`h-8 w-8 ${className}`}
    data-testid={dataTestId ?? `obligation-badge-${number}`}
    {...props}
  >
    {number}
  </Badge>
);

export const VulnerabilityBadge = ({ level, className = "", "data-testid": dataTestId, ...props }) => (
  <Badge
    variant={`vuln_${level}`}
    size="xs"
    className={`h-8 w-8 ${className}`}
    data-testid={dataTestId ?? `vulnerability-badge-${level}`}
    {...props}
  >
    {level}
  </Badge>
);

export const CriticalityBadge = ({ level, className = "", "data-testid": dataTestId, ...props }) => {
  const variantMap = {
    Critical: "vuln_critical",
    High: "vuln_high",
    Medium: "vuln_medium",
  };

  const slug = level === "Critical" ? "critical" : level === "High" ? "high" : level === "Medium" ? "medium" : "default";

  return (
    <Badge
      variant={variantMap[level] || "default"}
      size="xs"
      className={`h-6 w-6 ${className}`}
      data-testid={dataTestId ?? `criticality-badge-${slug}`}
      {...props}
    >
      {level === "Critical" ? "C" : level === "High" ? "H" : "M"}
    </Badge>
  );
};

export default Badge;
